<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    v-if="open"
    @click.self="$emit('close')"
  >
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4"
    >
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Begroting {{ selectedYear }}
        </h3>
      </div>

      <form @submit.prevent="handleSubmit" class="p-6">
        <!-- Year selector -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Jaar
          </label>
          <select
            v-model.number="selectedYear"
            class="w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>

        <!-- Monthly budget table -->
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-gray-200 dark:border-gray-700">
              <th class="py-2 text-left text-gray-600 dark:text-gray-400 font-medium">Maand</th>
              <th class="py-2 text-right text-gray-600 dark:text-gray-400 font-medium">
                Uren omzet
              </th>
              <th class="py-2 text-right text-gray-600 dark:text-gray-400 font-medium">3e omzet</th>
              <th class="py-2 text-right text-gray-600 dark:text-gray-400 font-medium">
                Derden realisatie
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in monthRows"
              :key="idx"
              class="border-b border-gray-100 dark:border-gray-700/50"
            >
              <td class="py-2 text-gray-900 dark:text-white">{{ row.label }}</td>
              <td class="py-2">
                <input
                  v-model="row.budget_hours_revenue"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  class="no-spinners w-full text-right px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </td>
              <td class="py-2 pl-2">
                <input
                  v-model="row.budget_derden_omzet"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  class="no-spinners w-full text-right px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </td>
              <td class="py-2 pl-2">
                <input
                  v-model="row.actual_derden_omzet"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  class="no-spinners w-full text-right px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="font-semibold text-gray-900 dark:text-white">
              <td class="py-2">Totaal</td>
              <td class="py-2 text-right">{{ formatCurrency(totalHours) }}</td>
              <td class="py-2 text-right pl-2">{{ formatCurrency(totalDerden) }}</td>
              <td class="py-2 text-right pl-2">{{ formatCurrency(totalActualDerden) }}</td>
            </tr>
          </tfoot>
        </table>

        <!-- Error -->
        <p v-if="submitError" class="mt-4 text-sm text-red-600 dark:text-red-400">
          {{ submitError }}
        </p>

        <!-- Actions -->
        <div class="mt-6 flex justify-end gap-3">
          <button
            type="button"
            @click="$emit('close')"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Annuleren
          </button>
          <button
            type="submit"
            :disabled="isSaving"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {{ isSaving ? 'Opslaan...' : 'Opslaan' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const MONTH_LABELS = [
  'Januari',
  'Februari',
  'Maart',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Augustus',
  'September',
  'Oktober',
  'November',
  'December'
]

const props = defineProps({
  open: { type: Boolean, default: false },
  year: { type: Number, default: null },
  budgets: { type: Array, default: () => [] }
})

const emit = defineEmits(['close', 'save'])

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - 3 + i)

const selectedYear = ref(props.year || currentYear)
const isSaving = ref(false)
const submitError = ref('')

const monthRows = ref(createEmptyRows())

function createEmptyRows() {
  return MONTH_LABELS.map((label, i) => ({
    label,
    month: i + 1,
    budget_hours_revenue: null,
    budget_derden_omzet: null,
    actual_derden_omzet: null
  }))
}

function loadFromBudgets() {
  const rows = createEmptyRows()
  for (const b of props.budgets) {
    if (b.year === selectedYear.value) {
      const idx = b.month - 1
      if (idx >= 0 && idx < 12) {
        rows[idx].budget_hours_revenue = parseFloat(b.budget_hours_revenue) || null
        rows[idx].budget_derden_omzet = parseFloat(b.budget_derden_omzet) || null
        rows[idx].actual_derden_omzet = parseFloat(b.actual_derden_omzet) || null
      }
    }
  }
  monthRows.value = rows
}

// Reload when year changes or modal opens
watch(
  [() => props.open, selectedYear],
  () => {
    if (props.open) {
      submitError.value = ''
      loadFromBudgets()
    }
  },
  { immediate: true }
)

watch(
  () => props.year,
  newYear => {
    if (newYear) selectedYear.value = newYear
  }
)

const totalHours = computed(() =>
  monthRows.value.reduce((s, r) => s + (r.budget_hours_revenue || 0), 0)
)
const totalDerden = computed(() =>
  monthRows.value.reduce((s, r) => s + (r.budget_derden_omzet || 0), 0)
)
const totalActualDerden = computed(() =>
  monthRows.value.reduce((s, r) => s + (r.actual_derden_omzet || 0), 0)
)

function formatCurrency(amount) {
  return `\u20AC ${new Intl.NumberFormat('nl-NL').format(Math.round(amount))}`
}

async function handleSubmit() {
  isSaving.value = true
  submitError.value = ''
  try {
    await emit('save', {
      year: selectedYear.value,
      months: monthRows.value.map(r => ({
        month: r.month,
        budget_hours_revenue: r.budget_hours_revenue || 0,
        budget_derden_omzet: r.budget_derden_omzet || 0,
        actual_derden_omzet: r.actual_derden_omzet || 0
      }))
    })
  } catch (e) {
    submitError.value = e.message || 'Fout bij opslaan'
  } finally {
    isSaving.value = false
  }
}
</script>
